import { useCallback, useEffect, useMemo, useState } from "react";

import { usePersistState } from "@/hooks/usePersistState";
import useReferralsRedeem from "@/hooks/cred/useReferralsRedeem";
import { useSearchParams } from "next/navigation";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";

export default function useReferrerHandler(onSuccess: () => void, onFailure: (() => void) | undefined = undefined) {
  const { user } = useDynamicContext();
  const isAuthenticated = useMemo(() => user !== undefined, [user]);

  const [refAddress, setRefAddress] = useState<string | undefined>();
  const [isRedeemed, setIsRedeemed] = usePersistState<boolean>("referrer-redeemed", undefined);
  const [enableRedeem, setEnableRedeem] = useState(false);

  const { data, isLoading, isError } = useReferralsRedeem(refAddress, user && enableRedeem);

  const searchParams = useSearchParams();
  const ref = searchParams.get("ref");

  const handleSetCode = useCallback(() => {
    if (ref !== null && ref && !isRedeemed) {
      setRefAddress(ref);
    }
  }, [isRedeemed, ref]);

  const handleReferrerRedeem = useCallback(() => {
    setEnableRedeem(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated && refAddress && enableRedeem && !isLoading) {
      if (data?.success) {
        onSuccess();
        setRefAddress(undefined);
        setIsRedeemed(true);
      }
      if (isError) {
        onFailure?.();
        setRefAddress(undefined);
        setIsRedeemed(true);
      }
    }
  }, [data?.success, enableRedeem, isAuthenticated, isError, isLoading, onFailure, onSuccess, refAddress, setIsRedeemed]);

  const handleCancel = useCallback(() => {
    setRefAddress(undefined);
    setIsRedeemed(true);
  }, [setIsRedeemed]);

  return useMemo(
    () => ({
      hasRef: refAddress !== undefined,
      handleReferrerRedeem,
      handleSetCode,
      isLoading: isLoading,
      isAuthenticated,
      handleCancel,
    }),
    [handleCancel, handleReferrerRedeem, handleSetCode, isLoading, isAuthenticated, refAddress]
  );
}
